import React from "react";
import { useLocation } from "react-router-dom";
import { appHistory } from "@/router/history";

import { withAuthData, type AuthActions } from "./context/AuthContext";
import type { UserData } from "./types/state";

interface AuthenticatedWrappedProps {
  userLoggedIn?: boolean;
  customerLoggedIn?: boolean;
  userData?: UserData;
  adminToken?: string | boolean | null;
  location?: { pathname: string };
  route?: string | null;
  authActions: Pick<AuthActions, "setRedirect" | "unloadRedirect">;
  [key: string]: unknown;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default function AuthenticatedComponent(
  ComposedComponent: React.ComponentType<any>,
  _allowOnRestrictCustomer = false
): React.ComponentType<Record<string, unknown>> {
  function AuthenticatedWrapper(props: AuthenticatedWrappedProps) {
    const { userData, userLoggedIn, customerLoggedIn, adminToken, location, authActions } = props;
    const { setRedirect } = authActions;

    React.useEffect(() => {
      // The EULA must be accepted first, otherwise the user is sent to a page
      // that cannot render without a selected customer.
      if (userData && userData.must_change_password && userData.has_accepted_eula && !adminToken) {
        if (location?.pathname !== BASE_PATH + "/profile/parameters") {
          appHistory.push(BASE_PATH + "/profile/parameters");
        }
      } else if (!userLoggedIn) {
        if (location) {
          setRedirect(location.pathname);
        }
        if (adminToken) {
          appHistory.push(BASE_PATH + "/borrow_customer");
        } else {
          appHistory.push(BASE_PATH + "/login");
        }
      } else if (!customerLoggedIn) {
        appHistory.push(BASE_PATH + "/choose_customer");
      }
    }, [userData, userLoggedIn, customerLoggedIn, adminToken, location, setRedirect]);

    if (userLoggedIn && customerLoggedIn) {
      return <ComposedComponent {...(props as Record<string, unknown>)} />;
    }
    return null;
  }

  const WithAuth = withAuthData(AuthenticatedWrapper);

  function WithRouter(props: Record<string, unknown>) {
    const location = useLocation();
    return <WithAuth {...(props as Record<string, unknown>)} location={location} />;
  }

  return WithRouter as unknown as React.ComponentType<Record<string, unknown>>;
}
